import { ReactElement, useState } from "react";

import { NoteListItem, NotesList } from "@/Common/Components/NotesList/NotesList";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ButtonControl } from "../Buttons/ButtonControl";
import { SimpleTextareaResizable } from "../Inputs/SimpleTextareaResizable";
import { AnchoredPopup } from "./AnchoredPopup";

import styles from "./NotesPopup.module.scss";

/**
 * The properties for a {@link NotesPopup} component.
 */
interface NotesPopupProps {
    /**
     * The notes to list in the popup.
     */
    notes: NoteListItem[];

    /**
     * Whether the popup should be open now.
     */
    isOpen: boolean;

    /**
     * Whether the popup submit button should be disabled.
     */
    isSubmitDisabled: boolean;

    /**
     * The unique HTML ID for this specific popup. Defaults to an auto-generate ID unique within this session.
     */
    popupId: string;

    /**
     * The pixel position for the left edge of the popup relative to the top-left corner of the screen.
     */
    leftPositionPx: number;

    /**
     * The pixel position for the top edge of the popup relative to the top-left corner of the screen.
     */
    topPositionPx: number;

    /**
     * A callback invoked when the popup is closed using the cancel button or X icon.
     */
    onClose(): void;

    /**
     * A callback invoked when the submit button is clicked.
     * @param note The content of the note that was submitted.
     */
    onSubmitNoteClicked(note: string): void;

    /**
     * Optional data tag used for testing
     */
    dataCy?: string;

    /**
     * Optional data tag for note list used for testing
     */
    notesListDataCy?: string;

    /**
     * Optional data tag for textarea used for testing
     */
    textAreaDataCy?: string;
}

/**
 * A component which manages a popup to edit generic notes.
 * @param props The component properties.
 */
function NotesPopup(props: Readonly<NotesPopupProps>): ReactElement {
    const { getText } = useTranslation();

    const [note, setNote] = useState("");

    return (
        <AnchoredPopup
            onClose={() => props.onClose()}
            position={{ left: props.leftPositionPx, top: props.topPositionPx }}
            popupId={props.popupId}
            dataCy={props.dataCy}
        >
            <AnchoredPopup.Header
                title={getText("NotesLabel")}
            />
            <AnchoredPopup.Body>
                <div className={styles["notes-popup-notes-list"]}>
                    <NotesList
                        notes={props.notes}
                        dataCy={props.notesListDataCy}
                    />
                </div>
                <SimpleTextareaResizable
                    autoFocus
                    className={styles["notes-popup-text-area"]}
                    value={note}
                    onChange={event => setNote(event.target.value)}
                    rows={4}
                    data-cy={props.textAreaDataCy}
                />
            </AnchoredPopup.Body>
            <AnchoredPopup.Footer>
                <ButtonControl
                    variant="neutral"
                    dataCy="notes-popup-cancel-button"
                    label={getText("CancelLabel")}
                    onClick={props.onClose}
                />
                <ButtonControl
                    variant="brand"
                    dataCy="notes-popup-submit-button"
                    label={getText("SubmitLabel")}
                    disabled={note.trim().length === 0}
                    onClick={() => props.onSubmitNoteClicked(note)}
                />
            </AnchoredPopup.Footer>
        </AnchoredPopup>
    );
}

export { NotesPopup };
